import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Input, PageLoader } from "@/components/ui";
import { Avatar, PinPad } from "@/components/bits";
import { PasskeySignIn } from "@/components/PasskeySignIn";
import { api, ApiError } from "@/lib/api";
import type { DeviceFamilyDTO, MeDTO } from "@shared/types";
import { AuthLayout } from "./AuthLayout";
import { useKidDevice } from "./PairedKids";

/**
 * Kids sign in with their PIN on a device a parent paired. Pairing uses a one-time code the
 * parent creates (Settings → Kid devices); the device keeps it in a secure cookie.
 */
export function KidLogin() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const device = useKidDevice();
  const pairCode = params.get("pair");
  // Tapped on the sign-in screen: go straight to that kid's PIN pad.
  const tapped = params.get("kid");

  if (device.isLoading) return <PageLoader />;
  if (!device.data || pairCode) {
    return (
      <PairDevice
        initialCode={pairCode ?? ""}
        onPaired={(d) => {
          qc.setQueryData(["kid-device"], d);
          if (pairCode) setParams({}, { replace: true });
        }}
      />
    );
  }
  return <ChooseKid device={device.data} initialKid={tapped} onForget={() => qc.setQueryData(["kid-device"], null)} />;
}

function PairDevice({ initialCode, onPaired }: { initialCode: string; onPaired: (d: DeviceFamilyDTO) => void }) {
  const [code, setCode] = useState(initialCode.toUpperCase());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function pair(value: string) {
    setBusy(true);
    setError(null);
    try {
      onPaired(await api.post<DeviceFamilyDTO>("/auth/device/pair", { code: value }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not pair this device");
    } finally {
      setBusy(false);
    }
  }

  // Opened from the parent's pairing link or QR code: pair right away.
  useEffect(() => {
    if (initialCode) void pair(initialCode);
  }, []);

  return (
    <AuthLayout title="Set up this device" subtitle="Ask a parent to open Settings → Kid devices → Pair a device, then type the code here.">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void pair(code);
        }}
        className="space-y-4"
      >
        <Input
          aria-label="Pairing code"
          placeholder="ABCD-EFGH"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          className="text-center text-2xl font-black tracking-[0.2em]"
          maxLength={12}
        />
        {error && <p className="text-center font-bold text-danger">{error}</p>}
        <Button type="submit" size="lg" block disabled={code.replace(/[^A-Z0-9]/g, "").length < 8} loading={busy}>
          Pair this device
        </Button>
      </form>
    </AuthLayout>
  );
}

function ChooseKid({ device, initialKid, onForget }: { device: DeviceFamilyDTO; initialKid: string | null; onForget: () => void }) {
  const [kidId, setKidId] = useState<string | null>(
    device.kids.find((k) => k.id === initialKid)?.id ?? (device.kids.length === 1 ? device.kids[0].id : null),
  );
  const [error, setError] = useState<string | null>(null);
  const [pinError, setPinError] = useState(0);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  function signedIn(me: MeDTO) {
    qc.setQueryData(["me"], me);
    navigate(me.user.role === "parent" ? "/parent" : "/kid", { replace: true });
  }

  async function login(pin: string) {
    if (!kidId) return;
    setBusy(true);
    setError(null);
    try {
      signedIn(await api.post<MeDTO>("/auth/kid-login", { kidId, pin }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not sign in");
      setPinError((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  async function forget() {
    if (!window.confirm("Unpair this device? A parent will need to pair it again before kids can sign in here.")) return;
    await api.post("/auth/device/forget").catch(() => {});
    onForget();
  }

  if (!device.consentGranted) {
    return (
      <AuthLayout title="Almost ready!" subtitle={`${device.family.name} ${device.family.currencyIcon}`}>
        <p className="rounded-3xl bg-sunken p-5 text-center text-muted">
          A parent needs to finish setting things up before you can sign in. Ask them to check their email.
        </p>
      </AuthLayout>
    );
  }

  const kid = device.kids.find((k) => k.id === kidId);
  if (kid) {
    return (
      <AuthLayout title={`Hi ${kid.name}!`} subtitle={kid.hasPin ? "Enter your secret PIN." : "Ask a parent to set your PIN first."} back={initialKid ? "/login" : "/kid-login"}>
        <div className="flex flex-col items-center">
          <Avatar member={kid} size="xl" />
          {kid.hasPin && (
            <div className="mt-8 w-full">
              <PinPad onComplete={login} disabled={busy} error={pinError > 0 && !!error} key={pinError} />
            </div>
          )}
          {error && <p className="mt-4 text-center font-bold text-danger">{error}</p>}
          <PasskeySignIn className="mt-4 w-full" onDone={signedIn} />
          {device.kids.length > 1 && (
            <Button variant="ghost" className="mt-4" onClick={() => setKidId(null)}>
              Not {kid.name}? Switch
            </Button>
          )}
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Who's playing?" subtitle={`${device.family.name} ${device.family.currencyIcon}`}>
      {device.kids.length === 0 ? (
        <p className="rounded-3xl bg-sunken p-5 text-center text-muted">No kids yet — ask a parent to add you in the app.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {device.kids.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => setKidId(k.id)}
              className="flex flex-col items-center gap-2 rounded-3xl bg-card p-5 shadow-card ring-1 ring-line transition active:scale-95"
            >
              <Avatar member={k} size="lg" />
              <span className="text-lg font-extrabold">{k.name}</span>
            </button>
          ))}
        </div>
      )}
      <PasskeySignIn className="mt-4" onDone={signedIn} />
      <p className="mt-6 text-center text-sm text-muted">
        Parent?{" "}
        <Link to="/login" className="font-bold text-brand">
          Sign in
        </Link>
      </p>
      <Button variant="ghost" className="mt-2" block onClick={forget}>
        Unpair this device
      </Button>
    </AuthLayout>
  );
}
